import logApi from '@/utils/service/log'
import store from '@/store'
import { isWebview } from '@/utils/storeId'
import { formatDate } from '@/utils/date'

const getErrorSource = () => (isWebview() ? 'POS_APP' : 'POS_WEB')

/**
 * 最後看過的 storeId，只在當下取不到時當退路。
 */
let lastKnownStoreId = null

/** 警告：非致命、可追蹤的異常狀態 */
const WARN_EVENTS = new Set([
	'列印推送店家不符',
	'POS_WEB未連線出單機',
	'印表機斷線',
	'連線檢查失敗',
	'狀態查詢逾時',
	'狀態查詢失敗',
	'印表機未知狀態',
	// 與上面三個狀態查詢事件同一類，漏掉會讓「列印中途斷線」在日誌裡看起來像正常完成
	'狀態查詢中斷',
	// Android 第二台不印發票的告警（該處註解本來就寫明要留告警紀錄）
	'第二台發票未列印',
])

/** 錯誤：列印失敗或無法繼續 */
const ERROR_EVENTS = new Set([
	'列印推送解析失敗',
	'轉交Native失敗',
	'印表機未連線',
	'連線檢查異常',
	'列印異常',
	'Native列印處理異常',
	'BILL單格式錯誤',
	'NativeBILL單格式錯誤',
	'藍牙寫入失敗',
	'BILL單寫入失敗',
	'發票寫入失敗',
	'單張BILL單列印失敗',
	'Native單張BILL單列印失敗',
	'BILL單資料異常',
	'標籤資料異常',
	'標籤機列印異常',
	// 整張單跑完但結果不是 ok（缺紙、寫入失敗、機器回報異常）。
	// 這是「交付了卻沒印出來」唯一的訊號，補送不會處理，只能人工補印，故列為 E-。
	'整張單列印未完成',
	// 佇列裡的單被丟棄。這些單早就回報過交付、後端不會補送，等於永久遺失，
	// 只剩這一筆紀錄；若落在 I-／W- 就會被只開錯誤日誌的店家整批濾掉。
	'佇列單據遺失',
	// 交付回報沒送到後端，那張單會被補送到上限並標成假的 FAILED，屬於要追的錯誤
	'列印已交付回報失敗',
	// 同上：缺 storeId／messageId 而根本沒送出，後果一樣是回報遺失，且代表串接有問題
	'列印已交付回報略過',
	// activate() 拋錯的分支會把 socket／stompClient 都清成 null 且不排任何重連，
	// 是整條 WS 流程裡唯一「連線死掉且不會自動復原」的路徑，漏收就完全查不到
	'WebSocket啟動失敗',
	'WebSocket心跳發送失敗',
	'WebSocket STOMP錯誤',
	'WebSocket連線關閉',
	'WebSocket觸發重連',
	'WebSocket重連超過上限',
	'WebSocket開始重連流程',
	'WebSocket執行disconnect',
])

/** App WebView 內僅由 Web 層上傳的 WS / 橋接邊界事件；其餘列印執行 log 交給 Native */
const WEB_LAYER_EVENTS_IN_APP = new Set([
	'收到列印推送',
	'列印推送解析失敗',
	'列印推送店家不符',
	'轉交Native列印',
	'印表機未連線',
	'轉交Native失敗',
	// 交付回報這三個事件是 Web 層自己發的（native 只負責觸發），
	// 不列在這裡的話，APP 內會在 fromNative 判斷就被丟掉，
	// 於是整套機制唯一的失敗訊號在裝置端不會留下任何痕跡。
	'列印已交付回報失敗',
	'列印已交付回報略過',
	'列印重送已交付略過',
	'WebSocket啟動失敗',
	'WebSocket心跳發送失敗',
	'WebSocket STOMP錯誤',
	'WebSocket連線關閉',
	'WebSocket觸發重連',
	'WebSocket重連超過上限',
	'WebSocket開始重連流程',
	'WebSocket執行disconnect',
])

export const getBillLength = data => {
	if (data == null) return 0
	if (typeof data === 'string') return data.length
	if (Array.isArray(data)) return data.length
	if (typeof data.length === 'number') return data.length
	return 0
}

export const hasPrintableBillData = data => getBillLength(data) > 0

const formatEvent = event => {
	if (/^[IWE]-/.test(event)) {
		return event
	}
	if (ERROR_EVENTS.has(event)) {
		return `E-${event}`
	}
	if (WARN_EVENTS.has(event)) {
		return `W-${event}`
	}
	return `I-${event}`
}

export const getReceiptMeta = data => ({
	storeReceipt: data?.storeReceipt?.length ?? 0,
	kitchenReceipt: data?.kitchenReceipt?.length ?? 0,
	clientReceipt: data?.clientReceipt?.length ?? 0,
	invoice: data?.invoice?.length ?? 0,
})

export const getPrinterConnected = (rootState, machineType) => {
	if (machineType === 'pos') return !!rootState.order.BTPosPrinter
	if (machineType === 'posSecond') return !!rootState.order.BTPosPrinterSecond
	if (machineType === 'kiosk') return !!rootState.order.BTKioskPrinter
	if (machineType === 'tag') return !!rootState.order.BTTagPrinter
	return false
}

/**
 * I- 資訊、W- 警告：僅在 printWarnLogSwitch 開啟時上傳
 * E- 錯誤：僅在 printErrorLogSwitch 開啟時上傳
 *
 * App 環境：Web 層只上傳 WS 入口與橋接失敗；列印執行 log 由 Native 轉回（fromNative）
 * Web 瀏覽器：維持 Web 層完整上傳
 *
 * @param {string} event 事件名稱（不含前綴，由本函式自動加上 I-/W-/E-）
 * @param {object} meta
 * @param {{ fromNative?: boolean }} [options]
 */
export const reportPrintLog = (event, meta = {}, options = {}) => {
	const { fromNative = false } = options

	if (isWebview() && !fromNative && !WEB_LAYER_EVENTS_IN_APP.has(event)) {
		return
	}

	const formattedEvent = formatEvent(event)
	const isError = formattedEvent.startsWith('E-')
	const isWarnOrInfo =
		formattedEvent.startsWith('W-') || formattedEvent.startsWith('I-')

	if (isWarnOrInfo && !store.state.storage?.printWarnLogSwitch) {
		return
	}

	if (isError && !store.state.storage?.printErrorLogSwitch) {
		return
	}

	const currentStoreId = store.state.storage?.storeId
	if (currentStoreId != null) {
		lastKnownStoreId = currentStoreId
	}
	const useFallbackStoreId = currentStoreId == null && lastKnownStoreId != null

	const payload = {
		event: formattedEvent,
		storeId: useFallbackStoreId ? lastKnownStoreId : currentStoreId,
		// 明講這筆是退路來的，避免把「登出後才送達的斷線」誤讀成該店當下仍在線
		...(useFallbackStoreId ? { storeIdFallback: true } : {}),
		...meta,
		timestamp: formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss'),
	}

	logApi
		.reportPrint({
			source: getErrorSource(),
			message: JSON.stringify(payload),
		})
		.catch(() => {})
}
